import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { ArrowRight, Flame, Target, BarChart3, Clock, Play, Zap, Brain, Crown, Lock, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [profile, setProfile] = useState(null);
  const [adaptiveSettings, setAdaptiveSettings] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, progsRes, profRes, settRes, recRes] = await Promise.all([
          axios.get(`${API}/attempts/stats`, { withCredentials: true }),
          axios.get(`${API}/programs`),
          axios.get(`${API}/adaptive/profile`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/adaptive/settings`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/adaptive/recommend`, { withCredentials: true }).catch(() => ({ data: { recommendations: [] } }))
        ]);
        setStats(statsRes.data);
        setPrograms(progsRes.data);
        setProfile(profRes.data);
        setAdaptiveSettings(settRes.data);
        setRecommendations(recRes.data.recommendations || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
        <div className="mono-data text-lg animate-pulse-red">Loading Dashboard...</div>
      </div>
    );
  }

  const hasActivity = stats && stats.total_sessions > 0;
  const freePrograms = programs.filter(p => p.is_free);
  const premiumPrograms = programs.filter(p => !p.is_free);
  const isPro = user?.subscription?.plan === 'pro' || user?.subscription?.plan === 'enterprise';

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      {/* Header */}
      <div className="border-b border-[#09090B] py-8">
        <div className="container-swiss flex items-center justify-between">
          <div>
            <p className="mono-data text-[#09090B] mb-1">Dashboard</p>
            <h1
              className="text-3xl md:text-4xl font-bold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="dashboard-greeting"
            >
              {user?.name ? `Welcome, ${user.name.split(' ')[0]}` : 'Welcome back'}
            </h1>
          </div>
          <div className="mono-data text-sm text-zinc-500" data-testid="subscription-badge">
            {user?.subscription?.plan === 'free' ? 'FREE PLAN' : 'PRO PLAN'}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      {hasActivity ? (
        <div className="border-b border-[#09090B]">
          <div className="container-swiss">
            <div className="dashboard-grid">
              <div className="grid-border-item" data-testid="stat-sessions">
                <div className="label-swiss mb-2">Sessions Completed</div>
                <div className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stats.total_sessions}
                </div>
              </div>
              <div className="grid-border-item" data-testid="stat-score">
                <div className="label-swiss mb-2">Average Score</div>
                <div className="text-4xl font-black tracking-tighter text-[#09090B]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stats.average_score}%
                </div>
              </div>
              <div className="grid-border-item" data-testid="stat-accuracy">
                <div className="label-swiss mb-2">Recall Accuracy</div>
                <div className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stats.average_accuracy}%
                </div>
              </div>
              <div className="grid-border-item" data-testid="stat-streak">
                <div className="label-swiss mb-2">Current Streak</div>
                <div className="text-4xl font-black tracking-tighter text-[#09090B] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Flame className="w-8 h-8" /> {stats.current_streak}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="border-b border-[#09090B]">
          <div className="container-swiss py-16 md:py-24 text-center">
            <div className="max-w-md mx-auto">
              <Clock className="w-12 h-12 text-[#09090B] mx-auto mb-6" />
              <h2
                className="text-2xl font-bold tracking-tight mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="empty-state-heading"
              >
                Start Your First Session
              </h2>
              <p className="text-zinc-600 mb-8">
                Your cognitive training journey begins with a single 1-hour session. Pick a program and begin.
              </p>
              <Link
                to="/programs/prog_memory_free"
                className="btn-accent inline-flex items-center gap-3"
                data-testid="start-first-session-btn"
              >
                <Play className="w-4 h-4" /> Begin Memory Awakening
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Adaptive Insights */}
      {hasActivity && profile && Object.keys(profile).length > 0 && (
        <div className="border-b border-[#09090B]">
          <div className="container-swiss py-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Brain className="w-5 h-5 text-[#09090B]" /> Cognitive Insights
              </h2>
              <Link to="/profile" className="mono-data text-sm text-[#09090B] flex items-center gap-1" data-testid="view-full-profile">
                Full Profile <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#09090B]">
              {/* Adaptive Mode */}
              <div className="p-6 border-r border-b md:border-b-0 border-[#09090B]" data-testid="adaptive-mode-card">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-[#09090B]" />
                  <span className="mono-data text-xs">Adaptive Mode</span>
                </div>
                <div className="text-2xl font-black tracking-tighter uppercase mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {adaptiveSettings?.difficulty_level || 'Medium'}
                </div>
                <p className="text-xs text-zinc-500">
                  {adaptiveSettings?.difficulty_level === 'easy'
                    ? 'Sessions simplified for skill building'
                    : adaptiveSettings?.difficulty_level === 'hard'
                    ? 'Maximum challenge enabled'
                    : 'Balanced difficulty for steady progress'}
                </p>
              </div>

              {/* Strengths */}
              <div className="p-6 border-r border-b md:border-b-0 border-[#09090B]" data-testid="strengths-card">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4 text-[#09090B]" />
                  <span className="mono-data text-xs">Strengths</span>
                </div>
                {profile.strong_areas?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {profile.strong_areas.map(a => (
                      <span key={a} className="px-2 py-1 bg-zinc-100 border border-zinc-300 text-[#09090B] text-xs font-medium capitalize">{a}</span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-400">Keep training to identify strengths</p>
                )}
              </div>

              {/* Focus Areas */}
              <div className="p-6" data-testid="focus-areas-card">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4 text-[#09090B]" />
                  <span className="mono-data text-xs">Focus Areas</span>
                </div>
                {profile.weak_areas?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {profile.weak_areas.map(a => (
                      <span key={a} className="px-2 py-1 bg-zinc-100 border border-zinc-300 text-[#09090B] text-xs font-medium capitalize">{a}</span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-400">No weak areas detected!</p>
                )}
              </div>
            </div>

            {/* Recommendations */}
            {recommendations.length > 0 && (
              <div className="mt-4">
                <div className="mono-data text-xs text-zinc-500 mb-2">Recommendations</div>
                <div className="flex flex-wrap gap-2">
                  {recommendations.slice(0, 3).map((rec, i) => (
                    <Link
                      key={i}
                      to={rec.session_id ? `/session/${rec.session_id}` : '/programs'}
                      className="border border-[#09090B] px-4 py-2 flex items-center gap-2 text-sm hover:bg-zinc-50 transition-colors"
                      data-testid={`dash-rec-${i}`}
                    >
                      <span className={`w-2 h-2 ${rec.priority === 'high' ? 'bg-[#09090B]' : 'bg-[#09090B]'}`} />
                      <span>{rec.title}</span>
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {hasActivity && stats.recent_attempts.length > 0 && (
        <div className="border-b border-[#09090B]">
          <div className="container-swiss py-8">
            <h2
              className="text-xl font-semibold tracking-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="recent-activity-heading"
            >
              Recent Activity
            </h2>
            <div className="border border-[#09090B]">
              {stats.recent_attempts.slice(0, 5).map((a, i) => (
                <div
                  key={a.attempt_id}
                  className={`flex items-center justify-between p-4 ${
                    i < stats.recent_attempts.length - 1 ? 'border-b border-[#09090B]' : ''
                  }`}
                  data-testid={`recent-attempt-${a.attempt_id}`}
                >
                  <div className="flex items-center gap-4">
                    <BarChart3 className="w-4 h-4 text-[#09090B]" />
                    <span className="text-sm font-medium">{a.session_id}</span>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className="mono-data text-xs">Score: {a.score}%</span>
                    <span className="mono-data text-xs text-zinc-400">
                      {new Date(a.completed_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PRO UPSELL — only for non-Pro users */}
      {!isPro && (
        <section className="border-b border-[#09090B] bg-[#09090B] text-white" data-testid="dashboard-pro-upsell">
          <div className="container-swiss py-12 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-2 mb-4">
                  <Crown className="w-4 h-4" />
                  <p className="mono-data text-zinc-400">Unlock Premium</p>
                </div>
                <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  21 premium programs are waiting.
                </h2>
                <p className="text-zinc-300 leading-relaxed max-w-xl">
                  Free programs cover the basics. Pro unlocks the full library — Advanced Memory Architecture, Speed Memory Systems, Executive Decision Making, Strategic Presentation Skills, and 17 more — plus AI coaching that adapts to your pace.
                </p>
                <div className="flex items-baseline gap-3 mt-6">
                  <span className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'JetBrains Mono, monospace' }}>₹299</span>
                  <span className="mono-data text-xs text-zinc-400">/ 30 days · one-time</span>
                </div>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Link
                  to="/pricing"
                  className="bg-white text-[#09090B] px-6 py-4 text-sm font-bold tracking-widest uppercase hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2"
                  data-testid="dash-upgrade-pro-btn"
                >
                  <Crown className="w-4 h-4" /> Upgrade to Pro <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/pricing"
                  className="border border-zinc-600 text-white px-6 py-4 text-sm font-bold tracking-widest uppercase hover:bg-white hover:text-[#09090B] transition-colors flex items-center justify-center gap-2"
                  data-testid="dash-view-pricing-btn"
                >
                  See all plans
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Programs split: Free + Premium */}
      <div className="container-swiss py-12">
        {/* Free programs */}
        <div className="flex items-center justify-between mb-8">
          <h2
            className="text-xl font-semibold tracking-tight flex items-center gap-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            <Sparkles className="w-4 h-4" /> Free Programs
          </h2>
          <Link to="/programs" className="mono-data text-sm text-[#09090B] flex items-center gap-1" data-testid="dash-view-all-programs">
            View All <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-[#09090B] mb-12">
          {freePrograms.slice(0, 3).map(p => (
            <Link
              to={`/programs/${p.program_id}`}
              key={p.program_id}
              className="border-r border-b border-[#09090B] p-6 group hover:bg-zinc-50 transition-colors"
              data-testid={`dash-program-${p.program_id}`}
            >
              <span className="mono-data text-xs text-[#09090B]">{p.category}</span>
              <h3
                className="text-lg font-semibold tracking-tight mt-2 mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {p.title}
              </h3>
              <p className="text-sm text-zinc-600 line-clamp-2">{p.description}</p>
              <div className="mt-4 mono-data text-xs flex items-center gap-1">
                {p.session_count} sessions <ArrowRight className="w-3 h-3" />
              </div>
            </Link>
          ))}
        </div>

        {/* Premium programs */}
        {premiumPrograms.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-8">
              <h2
                className="text-xl font-semibold tracking-tight flex items-center gap-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                <Crown className="w-4 h-4" /> Premium Programs
                <span className="mono-data text-xs text-zinc-400 ml-2 font-normal">{premiumPrograms.length} available</span>
              </h2>
              <Link to="/programs" className="mono-data text-sm text-[#09090B] flex items-center gap-1" data-testid="dash-view-all-premium">
                View All <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-[#09090B]" data-testid="dash-premium-grid">
              {premiumPrograms.slice(0, 6).map(p => (
                <Link
                  to={`/programs/${p.program_id}`}
                  key={p.program_id}
                  className="border-r border-b border-[#09090B] p-6 group hover:bg-zinc-50 transition-colors relative"
                  data-testid={`dash-premium-${p.program_id}`}
                >
                  {!isPro && (
                    <div className="absolute top-4 right-4 flex items-center gap-1 mono-data text-[10px] text-zinc-500">
                      <Lock className="w-3 h-3" />
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="mono-data text-xs text-[#09090B]">{p.category}</span>
                    <span className="mono-data text-xs text-zinc-400">·</span>
                    <span className="mono-data text-xs text-zinc-500">{p.session_count} × 1h</span>
                  </div>
                  <h3
                    className="text-lg font-semibold tracking-tight mt-1 mb-2 pr-6"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {p.title}
                  </h3>
                  <p className="text-sm text-zinc-600 line-clamp-2 mb-4">{p.description}</p>
                  <div className="pt-3 border-t border-zinc-200 flex items-center justify-between">
                    <span className="text-sm font-black tracking-tighter" style={{ fontFamily: 'JetBrains Mono, monospace' }}>₹{p.price}</span>
                    <span className="mono-data text-[10px] text-zinc-500 group-hover:text-[#09090B] transition-colors">
                      {isPro ? 'Included in Pro' : 'Buy or unlock with Pro'}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
